import {
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerContentProps,
  DrawerFooter,
  DrawerHeader,
  FormControl,
  FormLabel,
  Stack,
  Switch,
  Text,
} from '@chakra-ui/react';
import {
  useFetchAfeDetailsQuery,
  useFetchAfesPageQuery,
  useFetchUsersPageQuery,
} from '../../api';
import {
  AfeAutoComplete,
  AppInput,
  FormButtonGroup,
  UserAutoComplete,
} from '../components';
import { DatePicker } from '../form_fields';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { PutWorkOrderRequest } from '../../api/bindings/PutWorkOrderRequest';
import { useAuth } from '../../core/hooks';

export interface WorkOrderUpdateFormDrawerContentProps
  extends Omit<DrawerContentProps, 'onSubmit'> {
  afeId: string;
  workOrder: PutWorkOrderRequest;
  onSubmit: (form: PutWorkOrderRequest & { includeRevision: boolean }) => void;
  onClose: () => void;
}

interface Form extends PutWorkOrderRequest {
  includeRevision: boolean;
}

export const WorkOrderUpdateFormDrawerContent = (
  props: WorkOrderUpdateFormDrawerContentProps
) => {
  const { workOrder, afeId, onSubmit, onClose, ...rest } = props;

  // Fetches
  const auth = useAuth();
  // const { data: afes } = useFetchAfesPageQuery(
  //   { clientId: auth?.user.clientId ?? "0", all: true },
  //   { skip: !auth?.user.clientId }
  // );
  const { data: afe } = useFetchAfeDetailsQuery(afeId, { skip: !afeId });
  const { data: users } = useFetchUsersPageQuery(
    { clientId: auth?.user.clientId ?? '0', all: true },
    { skip: !auth?.user.clientId }
  );

  // State
  const form = useForm<Form>({
    mode: 'all',
    defaultValues: {
      id: workOrder.id,
      assigneeUserId: workOrder.assigneeUserId,
      name: workOrder.name,
      description: workOrder.description,
      reference: workOrder.reference,
      commitmentDate: workOrder.commitmentDate,
      includeRevision: false,
    },
  });
  const { control, handleSubmit, formState, register, watch } = form;

  const woName = watch('name');

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DrawerContent {...rest}>
          <DrawerCloseButton />
          <DrawerHeader>Update Work Order: {woName}</DrawerHeader>
          <DrawerBody>
            <Stack spacing={4}>
              <FormControl>
                <FormLabel htmlFor="afe">AFE:</FormLabel>
                <Text>{afe?.name}</Text>
              </FormControl>
              {/* <FormControl>
                <FormLabel htmlFor="afe">AFE:</FormLabel>
                <Controller
                  control={control}
                  name="afeId"
                  render={(p) => (
                    <AfeAutoComplete
                      id="afe"
                      items={afes?.items}
                      {...p.field}
                      isDisabled={true}
                      value={workOrder.afeId}
                    />
                  )}
                />
              </FormControl> */}
              <FormControl>
                <FormLabel htmlFor="name">Name:</FormLabel>
                <AppInput
                  id="name"
                  {...register('name', {
                    minLength: {
                      value: 3,
                      message: 'Name must be between 3 and 256 characters',
                    },
                    maxLength: {
                      value: 256,
                      message: 'Name must be between 3 and 256 characters',
                    },
                  })}
                />
              </FormControl>

              <FormControl>
                <FormLabel htmlFor="description">Description:</FormLabel>
                <AppInput
                  id="description"
                  {...register('description', {
                    minLength: {
                      value: 3,
                      message: 'Description is between 3 and 256 characters',
                    },
                    maxLength: {
                      value: 256,
                      message: 'Description is between 3 and 256 characters',
                    },
                  })}
                />
              </FormControl>
              <FormControl>
                <FormLabel htmlFor="reference">Reference:</FormLabel>
                <AppInput
                  id="reference"
                  {...register('reference', {
                    maxLength: {
                      value: 256,
                      message:
                        'Reference must be no greater than 256 characters',
                    },
                  })}
                />
              </FormControl>
              <FormControl>
                <FormLabel htmlFor="asignee">Asignee:</FormLabel>
                <Controller
                  control={control}
                  name="assigneeUserId"
                  rules={{
                    validate: (value) =>
                      value === null ||
                      BigInt(value) > 0 ||
                      'Please select a valid assignee',
                  }}
                  render={(p) => (
                    <UserAutoComplete
                      id="asignee"
                      items={users?.items}
                      {...p.field}
                    />
                  )}
                />
              </FormControl>
              <FormControl>
                <FormLabel>Commitment Date:</FormLabel>
                <DatePicker name="commitmentDate" control={control} />
              </FormControl>
              <FormControl>
                <FormLabel>Include a Revision?</FormLabel>
                <Controller
                  control={control}
                  name="includeRevision"
                  render={(p) => (
                    <Switch
                      size="lg"
                      colorScheme="blue"
                      isChecked={p.field.value}
                      onChange={() => p.field.onChange(!p.field.value)}
                    />
                  )}
                />
              </FormControl>
            </Stack>
          </DrawerBody>
          <DrawerFooter>
            <FormButtonGroup
              onCancel={onClose}
              onConfirm={handleSubmit(onSubmit)}
            />
          </DrawerFooter>
        </DrawerContent>
      </form>
    </FormProvider>
  );
};
